import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MODAL, ModalService } from '../modals/modal.service';

const LOGIN_ROUTE = '/connexion';

interface SessionNotice {
  title: string;
  message: string;
  validateBtn: string;
}

/**
 * Annonces de perte de session, partagées par l'`AuthGuardService` et l'intercepteur HTTP.
 *
 * Une même panne fait généralement échouer la navigation *et* plusieurs requêtes de la page :
 * le verrou est porté ici, pour qu'une seule popup s'affiche quelle que soit la porte d'entrée.
 */
@Injectable({ providedIn: 'root' })
export class SessionNoticeService {
  private noticeDisplayed = false;

  constructor(
    private readonly modalService: ModalService,
    // Le `Router` est résolu paresseusement : ce service est injecté dans un intercepteur HTTP,
    // construit avant que le routeur ne soit disponible.
    private readonly injector: Injector,
  ) {}

  /**
   * Le cookie AuthApp n'est plus exploitable : la session est perdue pour de bon, seule une
   * reconnexion la rétablira.
   */
  notifyExpiredSession(returnUrl = this.currentUrl()): void {
    this.notify(
      {
        title: 'Session expirée',
        message: 'Votre session a expiré. Merci de vous reconnecter pour poursuivre votre navigation.',
        validateBtn: 'Se reconnecter',
      },
      returnUrl,
      () => this.router.navigate([LOGIN_ROUTE], { queryParams: { returnUrl } }),
    );
  }

  /**
   * Panne transitoire du BFF : la session n'est pas perdue, elle n'a pas pu être vérifiée.
   * Réessayer la même navigation suffit une fois le service revenu.
   */
  notifyUnavailableService(returnUrl = this.currentUrl()): void {
    this.notify(
      {
        title: 'Service momentanément indisponible',
        message: 'Votre espace fidélité ne répond pas pour le moment. Merci de réessayer dans quelques instants.',
        validateBtn: 'Réessayer',
      },
      returnUrl,
      () => this.router.navigateByUrl(returnUrl),
    );
  }

  private notify(notice: SessionNotice, returnUrl: string, onClose: () => void): void {
    // Déjà sur la page de connexion : rien à annoncer, et surtout pas de returnUrl qui y ramène.
    if (this.noticeDisplayed || returnUrl.startsWith(LOGIN_ROUTE)) {
      return;
    }

    this.noticeDisplayed = true;
    // Bouton ou croix mènent au même endroit : rester sur place n'offrirait aucune issue.
    this.modalService.display(MODAL.GENERIC_POPUP, notice, () => {
      this.noticeDisplayed = false;
      onClose();
    });
  }

  private get router(): Router {
    return this.injector.get(Router);
  }

  private currentUrl(): string {
    return this.router.url;
  }
}
